import React, { useState, useRef } from 'react';
import {
  Button,
  TextField,
  Select,
  MenuItem,
  Box,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { CommunityBoardDAO } from '../../../../repository/CommunityBoardDAO';
import { useUserContext } from '../../../../context/user_context/UserContext';
import { useToast } from '../../../../context/toast/ToastContext';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { StringKeys } from '../../../../resources/strings/StringKeys';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import BaseModal from '../../../../components/modals/BaseModal';

// Import categories from BiziBoard
const CATEGORIES = {
  General: {
    bg: '#E3F2FD',
    text: '#1565C0'
  },
  Question: {
    bg: '#F3E5F5',
    text: '#7B1FA2'
  },
  Discussion: {
    bg: '#E8F5E9',
    text: '#2E7D32'
  },
  Event: {
    bg: '#E1F5FE',
    text: '#0277BD'
  },
  News: {
    bg: '#FFEBEE',
    text: '#C62828'
  },
  Help: {
    bg: '#E0F2F1',
    text: '#00695C'
  },
  Other: {
    bg: '#F3E5F5',
    text: '#6A1B9A'
  }
};

const AddPostModal = ({ show, onHide, parentPostId = null, onPostCreated }) => {
  const [topic, setTopic] = useState('');
  const [content, setContent] = useState('');
  const [category, setCategory] = useState('General');
  const [submitting, setSubmitting] = useState(false);
  const { firebaseUser } = useUserContext();
  const { showToast } = useToast();
  const { t } = useTranslation();
  const quillRef = useRef(null);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  const handleSubmit = async () => {
    if (!topic.trim() || !content.trim()) {
      showToast('Please fill in all fields', 'error');
      return;
    }

    if (!firebaseUser) {
      showToast('You must be logged in to create a post', 'error');
      return;
    }

    setSubmitting(true);

    try {
      const postData = {
        topic: topic.trim(),
        content: content.trim(),
        category,
        creator: `${firebaseUser.first_name || ''} ${firebaseUser.last_name || ''}`.trim() || 'Anonymous',
        creator_id: firebaseUser.uid,
        parent_id: parentPostId
      };

      var reply = await CommunityBoardDAO.createPost(postData);
      onPostCreated && onPostCreated();
      navigate(`/biziboard/${reply}`);
      handleClose();

    } catch (error) {
      console.error('Error creating post:', error);
      showToast('Failed to create post', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setTopic('');
    setContent('');
    setCategory('General');
    onHide();
  };

  const modalContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Select
        value={category}
        onChange={(e) => setCategory(e.target.value)}
        size="small"
      >
        {Object.entries(CATEGORIES).map(([cat, style]) => (
          <MenuItem 
            key={cat} 
            value={cat}
            sx={{
              '&.Mui-selected': {
                backgroundColor: style.bg,
                color: style.text,
                '&:hover': {
                  backgroundColor: style.bg,
                }
              }
            }}
          >
            {cat}
          </MenuItem>
        ))}
      </Select>

      <TextField
        label={t(StringKeys.TOPIC)}
        value={topic}
        onChange={(e) => setTopic(e.target.value)}
        fullWidth
        size="small"
      />

      <Box sx={{
        '& .ql-toolbar': {
          backgroundColor: (theme) => theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.background.paper,
          border: (theme) => `1px solid ${theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[300]}`,
          '& .ql-stroke': {
            stroke: (theme) => theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.common.black
          },
          '& .ql-fill': {
            fill: (theme) => theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.common.black
          },
          '& .ql-picker': {
            color: (theme) => theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.common.black
          }
        },
        '& .ql-container': {
          backgroundColor: (theme) => theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.background.paper,
          border: (theme) => `1px solid ${theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[300]}`,
          color: (theme) => theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.common.black
        }
      }}>
        <ReactQuill
          ref={quillRef}
          value={content}
          onChange={setContent}
          style={{ 
            height: '300px',
            marginBottom: '40px'
          }}
          theme="snow"
          modules={{
            toolbar: [
              [{ 'header': [1, 2, false] }],
              ['bold', 'italic', 'underline', 'strike', 'blockquote'],
              [{'list': 'ordered'}, {'list': 'bullet'}],
              ['link', 'image'],
              ['clean']
            ],
          }}
        />
      </Box>
    </Box>
  );

  const modalActions = (
    <>
      <Button onClick={handleClose}>
        {t(StringKeys.CANCEL)}
      </Button>
      <Button 
        variant="contained" 
        onClick={handleSubmit}
        disabled={submitting || !topic.trim() || !content.trim()}
      >
        {t(StringKeys.SUBMIT)}
      </Button>
    </>
  );

  return (
    <BaseModal
      open={show}
      onClose={handleClose}
      title={t(StringKeys.CREATE_POST)}
      maxWidth="md"
      children={modalContent}
      actions={modalActions}
    />
  );
};

export default AddPostModal;